import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
  Eye,
  EyeOff,
  ArrowRight,
} from 'lucide-react';

import { login, setToken } from '../services/api';
import { Button } from '../components/ui/button';
import controlCenterLogo from '../assets/control-center-logo.png';

interface TokenClaims {
  role?: string | string[];
}

const particles = [
  { left: '8%', top: '18%', delay: '0s', duration: '9s' },
  { left: '16%', top: '72%', delay: '2s', duration: '12s' },
  { left: '24%', top: '38%', delay: '5s', duration: '10s' },
  { left: '32%', top: '82%', delay: '1s', duration: '14s' },
  { left: '42%', top: '14%', delay: '4s', duration: '11s' },
  { left: '55%', top: '76%', delay: '3s', duration: '13s' },
  { left: '66%', top: '24%', delay: '6s', duration: '10s' },
  { left: '74%', top: '64%', delay: '2s', duration: '15s' },
  { left: '82%', top: '32%', delay: '5s', duration: '12s' },
  { left: '91%', top: '78%', delay: '1s', duration: '11s' },
];

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    if (loading) return;

    setLoading(true);
    setError('');

    try {
      const response = await login(email.trim(), password);

      const token = response.data.data.accessToken;

      const roles = jwtDecode<TokenClaims>(token).role;
      const roleList = Array.isArray(roles) ? roles : [roles];

      if (!roleList.includes('platform_operator')) {
        throw new Error('ACCESS_DENIED');
      }

      setToken(token);

      navigate('/', {
        replace: true,
      });
    } catch {
      /*
       * Намеренно не показываем причину ошибки:
       * - существует ли пользователь;
       * - неверен ли пароль;
       * - отсутствует ли роль;
       * - заблокирована ли учётная запись.
       */
      setError('Не удалось выполнить вход');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes secure-grid-drift {
          0% {
            transform: translate3d(0, 0, 0);
          }

          50% {
            transform: translate3d(-22px, -14px, 0);
          }

          100% {
            transform: translate3d(0, 0, 0);
          }
        }

        @keyframes secure-orbit {
          from {
            transform: translate(-50%, -50%) rotate(0deg);
          }

          to {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }

        @keyframes secure-orbit-reverse {
          from {
            transform: translate(-50%, -50%) rotate(360deg);
          }

          to {
            transform: translate(-50%, -50%) rotate(0deg);
          }
        }

        @keyframes secure-particle {
          0% {
            transform: translateY(0) scale(0.8);
            opacity: 0;
          }

          15% {
            opacity: 0.6;
          }

          50% {
            transform: translateY(-32px) scale(1);
            opacity: 0.9;
          }

          85% {
            opacity: 0.4;
          }

          100% {
            transform: translateY(-70px) scale(0.8);
            opacity: 0;
          }
        }

        @keyframes secure-stream-right {
          0% {
            transform: translateX(-130%);
            opacity: 0;
          }

          15% {
            opacity: 1;
          }

          80% {
            opacity: 1;
          }

          100% {
            transform: translateX(240%);
            opacity: 0;
          }
        }

        @keyframes secure-stream-left {
          0% {
            transform: translateX(180%);
            opacity: 0;
          }

          15% {
            opacity: 0.8;
          }

          80% {
            opacity: 0.8;
          }

          100% {
            transform: translateX(-220%);
            opacity: 0;
          }
        }

        @keyframes secure-scan {
          0% {
            top: -20%;
            opacity: 0;
          }

          10% {
            opacity: 0.25;
          }

          50% {
            opacity: 0.45;
          }

          90% {
            opacity: 0.2;
          }

          100% {
            top: 120%;
            opacity: 0;
          }
        }

        @keyframes secure-card-in {
          from {
            opacity: 0;
            transform: translateY(18px) scale(0.985);
            filter: blur(4px);
          }

          to {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }

        @keyframes secure-logo-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(96, 165, 250, 0.06),
              0 0 30px rgba(37, 99, 235, 0.08);
          }

          50% {
            box-shadow:
              0 0 0 1px rgba(96, 165, 250, 0.12),
              0 0 44px rgba(37, 99, 235, 0.18);
          }
        }

        @keyframes secure-dot-pulse {
          0%, 100% {
            opacity: 0.45;
            transform: scale(0.8);
          }

          50% {
            opacity: 1;
            transform: scale(1);
          }
        }

        .secure-grid {
          animation: secure-grid-drift 18s ease-in-out infinite;
        }

        .secure-card {
          animation: secure-card-in 700ms
            cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .secure-logo {
          animation: secure-logo-pulse 4s ease-in-out infinite;
        }

        .secure-orbit {
          animation: secure-orbit 35s linear infinite;
        }

        .secure-orbit-reverse {
          animation: secure-orbit-reverse 44s linear infinite;
        }

        .secure-scan {
          animation: secure-scan 9s linear infinite;
        }

        .secure-stream-right {
          animation: secure-stream-right 11s linear infinite;
        }

        .secure-stream-left {
          animation: secure-stream-left 14s linear infinite;
        }

        .secure-dot {
          animation: secure-dot-pulse 2.5s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .secure-grid,
          .secure-card,
          .secure-logo,
          .secure-orbit,
          .secure-orbit-reverse,
          .secure-scan,
          .secure-stream-right,
          .secure-stream-left,
          .secure-dot {
            animation: none !important;
          }
        }
      `}</style>

      <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#030712] px-4 py-10 text-white">
        {/* Base radial lighting */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-1/2 h-[900px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-600/[0.035] blur-[100px]" />

          <div className="absolute -left-[260px] top-[8%] h-[700px] w-[700px] rounded-full bg-indigo-600/[0.045] blur-[140px]" />

          <div className="absolute -right-[300px] bottom-[-200px] h-[800px] w-[800px] rounded-full bg-cyan-500/[0.035] blur-[150px]" />
        </div>

        {/* Moving technical grid */}
        <div
          className="secure-grid pointer-events-none absolute -inset-[60px] opacity-[0.18]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
            maskImage:
              'radial-gradient(circle at center, black 0%, rgba(0,0,0,.7) 38%, transparent 78%)',
            WebkitMaskImage:
              'radial-gradient(circle at center, black 0%, rgba(0,0,0,.7) 38%, transparent 78%)',
          }}
        />

        {/* Huge abstract rotating rings */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="
              secure-orbit
              absolute
              left-1/2
              top-1/2
              h-[760px]
              w-[760px]
              rounded-full
              border
              border-blue-400/[0.055]
            "
          >
            <div className="absolute left-[10%] top-[17%] h-1.5 w-1.5 rounded-full bg-blue-400/50 shadow-[0_0_16px_rgba(96,165,250,.8)]" />

            <div className="absolute bottom-[21%] right-[7%] h-1 w-1 rounded-full bg-cyan-300/40" />
          </div>

          <div
            className="
              secure-orbit-reverse
              absolute
              left-1/2
              top-1/2
              h-[560px]
              w-[560px]
              rounded-full
              border
              border-dashed
              border-slate-400/[0.04]
            "
          />
        </div>

        {/* Horizontal moving streams */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-50">
          <div className="secure-stream-right absolute left-0 top-[19%] h-px w-[34vw] bg-gradient-to-r from-transparent via-blue-400/30 to-transparent" />

          <div
            className="secure-stream-right absolute left-0 top-[67%] h-px w-[24vw] bg-gradient-to-r from-transparent via-cyan-300/20 to-transparent"
            style={{
              animationDelay: '4s',
              animationDuration: '14s',
            }}
          />

          <div className="secure-stream-left absolute right-0 top-[34%] h-px w-[30vw] bg-gradient-to-r from-transparent via-blue-300/25 to-transparent" />

          <div
            className="secure-stream-left absolute right-0 top-[82%] h-px w-[22vw] bg-gradient-to-r from-transparent via-indigo-300/20 to-transparent"
            style={{
              animationDelay: '7s',
            }}
          />
        </div>

        {/* Scan beam */}
        <div className="secure-scan pointer-events-none absolute left-0 h-[180px] w-full bg-gradient-to-b from-transparent via-blue-400/[0.025] to-transparent" />

        {/* Tiny particles */}
        <div className="pointer-events-none absolute inset-0">
          {particles.map((particle, index) => (
            <span
              key={index}
              className="absolute h-[2px] w-[2px] rounded-full bg-blue-300"
              style={{
                left: particle.left,
                top: particle.top,
                opacity: 0,
                animationName: 'secure-particle',
                animationTimingFunction: 'ease-in-out',
                animationIterationCount: 'infinite',
                animationDelay: particle.delay,
                animationDuration: particle.duration,
                boxShadow: '0 0 8px rgba(147,197,253,.7)',
              }}
            />
          ))}
        </div>

        {/* Edge vignette */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_25%,rgba(3,7,18,.35)_70%,rgba(3,7,18,.85)_100%)]" />

        {/* Login card */}
        <section className="secure-card relative z-10 w-full max-w-[430px]">
          {/* top security status */}
          <div className="mb-5 flex justify-center">
            <div
              className="
                inline-flex
                items-center
                gap-2
                rounded-full
                border
                border-white/[0.08]
                bg-white/[0.035]
                px-3
                py-1.5
                text-[10px]
                font-semibold
                uppercase
                tracking-[0.17em]
                text-slate-400
                backdrop-blur-xl
              "
            >
              <span className="secure-dot h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Ограниченный доступ
            </div>
          </div>

          <form
            onSubmit={submit}
            aria-busy={loading}
            className="
              relative
              overflow-hidden
              rounded-[24px]
              border
              border-white/[0.09]
              bg-slate-950/70
              px-6
              py-8
              shadow-[0_32px_100px_rgba(0,0,0,.55)]
              backdrop-blur-2xl
              sm:px-9
              sm:py-10
            "
          >
            {/* subtle internal highlight */}
            <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-blue-300/35 to-transparent" />

            <div className="pointer-events-none absolute -top-24 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full bg-blue-500/[0.08] blur-[55px]" />

            {/* Logo */}
            <div className="relative mb-6 flex justify-center">
              <img
                src={controlCenterLogo}
                alt="Control Center"
                className="h-auto w-[220px] max-w-full object-contain"
              />
            </div>

            {/* Heading */}
            <div className="relative mb-8 text-center">
              <h1 className="text-[25px] font-semibold tracking-[-0.03em] text-white">
                Войти
              </h1>

              <p className="mx-auto mt-2 max-w-[310px] text-[13px] leading-5 text-slate-500">
                Авторизация необходима для продолжения
              </p>
            </div>

            <div className="relative space-y-5">
              {/* Email */}
              <div className="space-y-2">
                <label
                  htmlFor="operator-email"
                  className="text-[12px] font-medium text-slate-400"
                >
                  Электронная почта
                </label>

                <input
                  id="operator-email"
                  required
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  disabled={loading}
                  className="
                    h-12
                    w-full
                    rounded-xl
                    border
                    border-white/[0.08]
                    bg-black/20
                    px-4
                    text-[14px]
                    text-white
                    outline-none
                    transition-all
                    duration-200
                    placeholder:text-slate-700
                    hover:border-white/[0.13]
                    focus:border-blue-400/40
                    focus:bg-black/30
                    focus:ring-4
                    focus:ring-blue-500/[0.06]
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                  "
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <label
                  htmlFor="operator-password"
                  className="text-[12px] font-medium text-slate-400"
                >
                  Пароль
                </label>

                <div className="relative">
                  <input
                    id="operator-password"
                    required
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) =>
                      setPassword(event.target.value)
                    }
                    autoComplete="current-password"
                    disabled={loading}
                    className="
                      h-12
                      w-full
                      rounded-xl
                      border
                      border-white/[0.08]
                      bg-black/20
                      px-4
                      pr-12
                      text-[14px]
                      text-white
                      outline-none
                      transition-all
                      duration-200
                      placeholder:text-slate-700
                      hover:border-white/[0.13]
                      focus:border-blue-400/40
                      focus:bg-black/30
                      focus:ring-4
                      focus:ring-blue-500/[0.06]
                      disabled:cursor-not-allowed
                      disabled:opacity-50
                    "
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword((value) => !value)
                    }
                    disabled={loading}
                    className="
                      absolute
                      right-2
                      top-1/2
                      flex
                      h-8
                      w-8
                      -translate-y-1/2
                      items-center
                      justify-center
                      rounded-lg
                      text-slate-600
                      transition
                      hover:bg-white/[0.05]
                      hover:text-slate-300
                      focus-visible:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-blue-400/40
                      disabled:pointer-events-none
                    "
                    aria-label={
                      showPassword
                        ? 'Скрыть пароль'
                        : 'Показать пароль'
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Generic error */}
              {error && (
                <div
                  role="alert"
                  className="
                    rounded-xl
                    border
                    border-red-400/[0.15]
                    bg-red-400/[0.055]
                    px-4
                    py-3
                    text-center
                    text-[12px]
                    font-medium
                    text-red-300
                  "
                >
                  {error}
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="
                  group
                  relative
                  mt-2
                  h-12
                  w-full
                  overflow-hidden
                  rounded-xl
                  border
                  border-blue-400/10
                  bg-blue-600
                  text-sm
                  font-semibold
                  text-white
                  shadow-[0_8px_30px_rgba(37,99,235,.18)]
                  transition-all
                  duration-200
                  hover:bg-blue-500
                  hover:shadow-[0_10px_38px_rgba(37,99,235,.26)]
                  active:scale-[0.99]
                  disabled:pointer-events-none
                  disabled:opacity-60
                "
              >
                {/* button shimmer */}
                {!loading && (
                  <span
                    className="
                      pointer-events-none
                      absolute
                      inset-y-0
                      -left-20
                      w-16
                      -skew-x-12
                      bg-gradient-to-r
                      from-transparent
                      via-white/10
                      to-transparent
                      transition-transform
                      duration-700
                      group-hover:translate-x-[500px]
                    "
                  />
                )}

                {loading ? (
                  <span className="relative flex items-center justify-center gap-2.5">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-white" />
                    Проверка...
                  </span>
                ) : (
                  <span className="relative flex items-center justify-center gap-2">
                    Продолжить
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </span>
                )}
              </Button>
            </div>

            {/* Bottom security line */}
            {/*<div className="relative mt-7 flex items-center justify-center gap-2 border-t border-white/[0.055] pt-6 text-[10px] font-medium uppercase tracking-[0.13em] text-slate-600">
              <LockKeyhole className="h-3 w-3" />
                Доступ только для авторизованных пользователей
            </div>*/}
          </form>
        </section>

        {/* bottom decoration */}
        <div className="pointer-events-none absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-1.5 opacity-30">
          <span className="h-1 w-1 rounded-full bg-slate-500" />
          <span className="h-1 w-8 rounded-full bg-gradient-to-r from-slate-700 via-slate-500 to-slate-700" />
          <span className="h-1 w-1 rounded-full bg-slate-500" />
        </div>
      </main>
    </>
  );
};
